import { ColumnFilterValueSchema } from "@/schemas/commonSchema";
import type { FilterFn } from "@tanstack/react-table";
import type {
  DateRangeValue,
  MultiSelectValue,
  NumberRangeValue,
  SelectValue,
  TableUrlState,
  TextValue,
} from "./types/data-table";

export const createTextFilterFn =
  <T>(): FilterFn<T> =>
  (row, columnId, value: TextValue) => {
    const cellValue = row.getValue(columnId);
    if (typeof cellValue !== "string") return false;
    if (!value) return true;
    return cellValue.toLowerCase().includes(value.toLowerCase());
  };

export const createNumberRangeFilterFn =
  <T>(): FilterFn<T> =>
  (row, columnId, value: NumberRangeValue) => {
    const cellValue = Number(row.getValue(columnId));
    if (Number.isNaN(cellValue)) return false;
    if (!value) return true;

    const { min, max } = value;
    if (min !== undefined && cellValue < min) return false;
    if (max !== undefined && cellValue > max) return false;

    return true;
  };

export const createDateRangeFilterFn =
  <T>(): FilterFn<T> =>
  (row, columnId, value: DateRangeValue) => {
    const cellValue = row.getValue(columnId);
    if (!cellValue) return false;

    const date = new Date(cellValue as string);
    if (Number.isNaN(date.getTime())) return false;

    if (!value) return true;

    if (value.from && date < value.from) return false;
    if (value.to && date > value.to) return false;

    return true;
  };

export const createRadioSelectFilterFn =
  <T>(): FilterFn<T> =>
  (row, columnId, value: SelectValue) => {
    if (!value) return true;
    const cellValue = row.getValue(columnId);
    return cellValue === value;
  };

export const createMultiSelectFilterFn =
  <T>(): FilterFn<T> =>
  (row, columnId, value: MultiSelectValue) => {
    if (!value || value.length === 0) return true;
    const cellValue = row.getValue(columnId);
    return value.includes(cellValue as string);
  };

export function mapTableStateToQueryParams(
  state: TableUrlState,
  extra?: URLSearchParams,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  searchParams.set("limit", String(state.pagination?.pageSize ?? 10));
  searchParams.set("showDeleted", String(state.showDeleted ?? false));

  if (state.pagination?.cursor) {
    searchParams.set("cursor", state.pagination.cursor);
  }
  if (state.globalFilter) {
    searchParams.set("search", state.globalFilter);
  }

  const activeSort = state.sorting?.[0];
  if (activeSort) {
    searchParams.set("sortBy", activeSort.id);
    searchParams.set("sortOrder", activeSort.desc ? "desc" : "asc");
  }

  state.columnFilters?.forEach((filter) => {
    const result = ColumnFilterValueSchema.safeParse(filter.value);
    if (!result.success) return;

    const payload = result.data;

    switch (payload.type) {
      case "text":
      case "radio":
        if (payload.filterValue) {
          searchParams.set(filter.id, payload.filterValue);
        }
        break;

      case "select":
        if (payload.filterValue.length > 0) {
          searchParams.set(filter.id, payload.filterValue.join(","));
        }
        break;

      case "number": {
        const { min, max } = payload.filterValue;
        if (min !== undefined) searchParams.set(`${filter.id}Min`, String(min));
        if (max !== undefined) searchParams.set(`${filter.id}Max`, String(max));
        break;
      }

      case "date": {
        const { from, to } = payload.filterValue;
        if (from) searchParams.set(`${filter.id}From`, from.toISOString());
        if (to) searchParams.set(`${filter.id}To`, to.toISOString());
        break;
      }
    }
  });

  if (extra) {
    extra.forEach((value, key) => {
      searchParams.set(key, value);
    });
  }
  return searchParams;
}
